import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { clients as clientsApi } from '@/api/endpoints'
import { formatDate, formatMoney } from '@/lib/utils'

/**
 * Kredit zákazníka - vrátené platby pripísané na ďalšiu rezerváciu
 * (`apps.payments.models.ClientCredit`). Kým zákazník kredit nikdy nemal,
 * nezobrazí sa nič - pri väčšine klientov by to bol len riadok s nulou.
 */
export default function ClientCredit({ clientId }: { clientId: number }) {
  const { t } = useTranslation()
  const { data } = useQuery({
    queryKey: ['client-credit', clientId],
    queryFn: () => clientsApi.credit(clientId),
    enabled: Boolean(clientId),
  })

  if (!data || !data.movements.length) return null
  const balance = Number.parseFloat(data.balance)

  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3 text-sm">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-medium uppercase text-emerald-800">
          {t('payments.creditBalance')}
        </span>
        <span className="text-base font-bold text-emerald-700">{formatMoney(balance)}</span>
      </div>
      <div className="mt-2 text-xs font-medium uppercase text-slate-500">
        {t('payments.creditHistory')}
      </div>
      <ul className="mt-1 space-y-0.5">
        {data.movements.map((m) => (
          <li key={m.id} className="flex justify-between gap-3 text-slate-600">
            <span>
              {formatDate(m.created)}
              {m.ref ? <span className="ml-2 font-mono text-xs text-slate-400">{m.ref}</span> : null}
              {m.note ? <span className="ml-2">{m.note}</span> : null}
            </span>
            <span
              className={Number.parseFloat(m.amount) < 0 ? 'text-red-600' : 'text-emerald-700'}
            >
              {formatMoney(m.amount)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
